import axios from "axios";
import { supabase } from "./supabaseClient";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUCKET = "event-images";

// Generic automotive placeholder used when an event has no usable cover image.
// Not an official photo of any specific event.
export const PLACEHOLDER_IMAGE =
  "https://images.unsplash.com/photo-1503376780353-7e6692767b70?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

// External cover images (e.g. AI Admin external_image_url from WordPress/Wix/CDN
// sites) are routed through the weserv image proxy so hotlink/referrer/CORS
// restrictions don't break them. No copy is stored anywhere — the proxy fetches and
// re-serves on demand. Returns null for empty/non-http values (so callers fall back
// to the placeholder).
// Size-aware: cards/list views (the default) request a smaller ~600px image so many
// events on screen stay light; only the full event page cover (`hero`) requests a
// larger ~1200px image that stays sharp without loading the original full-res.
export function proxyExternalImage(url, { hero = false } = {}) {
  if (!url || typeof url !== "string") return null;
  const u = url.trim();
  if (!/^https?:\/\//i.test(u)) return null;
  const stripped = u.replace(/^https?:\/\//i, "");
  const w = hero ? 1200 : 600;
  const q = hero ? 72 : 65;
  return `https://images.weserv.nl/?url=${encodeURIComponent(stripped)}&w=${w}&q=${q}&output=jpg&we`;
}

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// ── Cover image cache/signer ────────────────────────────────────────────────
// Private bucket → transformed images must be signed individually. To keep this
// bounded as the DB grows, signing is lazy (per rendered card) and cached by
// storage_path, so repeated appearances (Near You / Featured / All Events) and
// re-renders reuse the same signed URL instead of re-signing.
const _coverCache = new Map(); // key -> { url, exp }
const _coverInflight = new Map(); // key -> promise
const _coverKey = (path, hero) => `${hero ? "h" : "c"}:${path}`;

// Signed links are also kept across app restarts (until they expire) so a cover the
// phone already downloaded is reused from its image cache instead of being fetched
// again from Supabase under a brand-new link (support #279162: egress).
const COVER_STORE_KEY = "roadrnr.coverUrls.v1";
try {
  const saved = JSON.parse(localStorage.getItem(COVER_STORE_KEY) || "{}");
  const nowMs = Date.now();
  Object.entries(saved).forEach(([k, v]) => { if (v && v.url && v.exp > nowMs) _coverCache.set(k, v); });
} catch (e) { /* storage unavailable: memory cache only */ }
let _coverSaveTimer = null;
function persistCovers() {
  clearTimeout(_coverSaveTimer);
  _coverSaveTimer = setTimeout(() => {
    try {
      const nowMs = Date.now();
      const fresh = [..._coverCache.entries()].filter(([, v]) => v.exp > nowMs).slice(-1500);
      localStorage.setItem(COVER_STORE_KEY, JSON.stringify(Object.fromEntries(fresh)));
    } catch (e) { /* quota / private mode: keep memory cache only */ }
  }, 1000);
}
// Signed cover URLs are temporary. Sign for a long window and cache WITH an expiry
// so a stale (expired) URL is re-signed on next use instead of serving a broken
// image after the app has been open a while. bustCover() forces a re-sign on error.
const COVER_SIGN_TTL = 6 * 3600; // seconds

export function cachedCover(path, hero = false) {
  if (!path) return null;
  const hit = _coverCache.get(_coverKey(path, hero));
  if (!hit || hit.exp <= Date.now()) return null;
  return hit.url;
}

export function bustCover(path, hero = false) {
  if (path) _coverCache.delete(_coverKey(path, hero));
}

export async function resolveCover(path, hero = false) {
  if (!path) return null;
  const k = _coverKey(path, hero);
  const hit = _coverCache.get(k);
  if (hit && hit.exp > Date.now()) return hit.url;
  if (_coverInflight.has(k)) return _coverInflight.get(k);
  const p = supabase.storage
    .from(BUCKET)
    .createSignedUrl(path, COVER_SIGN_TTL, { transform: { width: hero ? 1000 : 600, quality: hero ? 72 : 65, resize: "cover" } })
    .then(({ data }) => {
      const url = data?.signedUrl || null;
      if (url) {
        _coverCache.set(k, { url, exp: Date.now() + (COVER_SIGN_TTL - 300) * 1000 });
        persistCovers();
      }
      _coverInflight.delete(k);
      return url;
    })
    .catch(() => {
      _coverInflight.delete(k);
      return null;
    });
  _coverInflight.set(k, p);
  return p;
}

// Building an Intl.DateTimeFormat is expensive, and mapRow runs it twice per event for
// every event in every loader (support #279162: ~2s of CPU on a throttled phone), so
// keep one formatter per timezone and remember each converted timestamp.
const dtfByTz = new Map();
function dateFormatterFor(tz) {
  if (!dtfByTz.has(tz)) {
    let f = null;
    try {
      f = new Intl.DateTimeFormat("en-GB", {
        timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", hourCycle: "h23",
      });
    } catch (e) { /* unknown timezone: callers fall back to a raw split */ }
    dtfByTz.set(tz, f);
  }
  return dtfByTz.get(tz);
}

const splitIsoCache = new Map();
function splitIso(iso, tz) {
  if (!iso) return { date: "", time: "" };
  const key = `${tz || ""}|${iso}`;
  let hit = splitIsoCache.get(key);
  if (!hit) {
    hit = splitIsoUncached(iso, tz);
    if (splitIsoCache.size >= 20000) splitIsoCache.clear();
    splitIsoCache.set(key, hit);
  }
  return { date: hit.date, time: hit.time };
}

function splitIsoUncached(iso, tz) {
  // Display in the EVENT'S OWN timezone (start_at/end_at are absolute UTC instants).
  // Using Intl with the event's timezone gives the correct local wall-clock date/time
  // regardless of where the viewer is. Falls back to a raw split when no tz is known.
  if (tz) {
    const d = new Date(iso);
    const fmt = !isNaN(d.getTime()) ? dateFormatterFor(tz) : null;
    if (fmt) {
      try {
        const p = fmt.formatToParts(d).reduce((a, x) => { a[x.type] = x.value; return a; }, {});
        let time = `${p.hour}:${p.minute}`;
        if (time === "24:00") time = "00:00";
        if (time === "00:00") time = ""; // treat midnight as "no specific time"
        return { date: `${p.year}-${p.month}-${p.day}`, time };
      } catch (e) { /* fall through to raw split */ }
    }
  }
  const [datePart, rest] = String(iso).split("T");
  let time = rest ? rest.slice(0, 5) : "";
  if (time === "00:00") time = ""; // treat midnight as "no specific time"
  return { date: datePart || "", time };
}

function shortDate(ymd) {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-").map(Number);
  if (!y || !m || !d) return "";
  return `${d} ${MON[m - 1]} ${y}`;
}

// ── Occurrences (multiple event dates) ──────────────────────────────────────
// Additional dates for an event live in Event_Occurrences (event_id, start_at,
// end_at). The primary date stays on the Events row and Events.start_at/end_at are
// kept as the overall SPAN (earliest start / latest end) so the existing
// "not yet ended" discovery filter still works. Single-date events have no child
// rows and behave exactly as before.
export async function mapOccurrencesFor(eventIds, signal) {
  const result = {};
  if (!eventIds.length) return result;
  const data = await mapQueryPages(() => supabase.from("Event_Occurrences")
    .select("event_id,start_at,end_at").in("event_id", eventIds)
    .order("event_id").order("start_at").order("end_at"), signal);
  data.forEach((o) => {
    (result[o.event_id] = result[o.event_id] || []).push({ start_at: o.start_at, end_at: o.end_at });
  });
  return result;
}

// From a list of {start_at,end_at}, pick the CURRENT occurrence (live now) or else
// the NEXT upcoming; if all have ended, the latest. Drives displayed date + LIVE NOW.
export function pickCurrentNext(occs, nowMs = Date.now()) {
  const valid = (occs || [])
    .filter((o) => o.start_at && o.end_at)
    .map((o) => ({ ...o, s: new Date(o.start_at).getTime(), e: new Date(o.end_at).getTime() }))
    .filter((o) => Number.isFinite(o.s) && Number.isFinite(o.e));
  if (!valid.length) return { start_at: null, end_at: null };
  const live = valid.find((o) => o.s <= nowMs && nowMs < o.e);
  if (live) return { start_at: live.start_at, end_at: live.end_at };
  const upcoming = valid.filter((o) => o.s > nowMs).sort((a, b) => a.s - b.s);
  if (upcoming.length) return { start_at: upcoming[0].start_at, end_at: upcoming[0].end_at };
  const latest = [...valid].sort((a, b) => b.e - a.e)[0];
  return { start_at: latest.start_at, end_at: latest.end_at };
}

// Occurrences that have NOT yet ended (end > now), earliest first — for the Event
// Detail "More dates" list. Already-finished occurrences are excluded.
function upcomingOccurrences(occs, nowMs = Date.now()) {
  return (occs || [])
    .filter((o) => o.start_at && o.end_at)
    .map((o) => ({ ...o, s: new Date(o.start_at).getTime(), e: new Date(o.end_at).getTime() }))
    .filter((o) => Number.isFinite(o.e) && o.e > nowMs)
    .sort((a, b) => a.s - b.s)
    .map((o) => ({ start_at: o.start_at, end_at: o.end_at }));
}

// Resolve each event's current/next occurrence given a pre-fetched occurrences map.
function effectiveOf(row, occMap, now) {
  const occs = occMap[row.id]?.length ? occMap[row.id] : [{ start_at: row.start_at, end_at: row.end_at }];
  return pickCurrentNext(occs, now);
}

// Map a Supabase Events row into the shape the existing cards / detail expect.
// `opts.image` = an already-signed URL (used by Event Detail hero); `opts.coverPath`
// = the raw storage path for lazy per-card signing (used by list/rails).
function mapRow(row, opts = {}) {
  const { image = null, coverPath = null, start = null, end = null, occurrences = null, hero = false } = opts;
  // Effective (current/next) occurrence dates drive all displayed date/time and
  // LIVE NOW. Single-date events omit start/end and fall back to the row values.
  const startAt = start || row.start_at;
  const endAt = end || row.end_at;
  const s = splitIso(startAt, row.timezone);
  const e = splitIso(endAt, row.timezone);
  const lat = typeof row.latitude === "number" ? row.latitude : null;
  const lng = typeof row.longitude === "number" ? row.longitude : null;
  return {
    id: row.id,
    slug: row.slug,
    owner_id: row.owner_id || null,
    name: row.name,
    description: row.description || "",
    category: row.category,
    location: row.venue_name || row.address || row.postcode || "Location TBA",
    venue_name: row.venue_name || "",
    address: row.address || "",
    postcode: row.postcode || "",
    country_code: row.country_code || "",
    // Cover priority: signed Event_Images cover (via coverPath) → external_image_url
    // (rendered through an external image proxy so WordPress/Wix/CDN hotlink/referrer
    // restrictions don't break it) → generic placeholder. `image` arg is the already-
    // signed Event_Images URL (detail hero) when present.
    image: image || proxyExternalImage(row.external_image_url, { hero }) || PLACEHOLDER_IMAGE,
    // Raw (un-proxied) external URL kept alongside the proxied `image` so the UI can
    // automatically retry the original if the weserv proxy is rejected by the origin.
    imageRaw: row.external_image_url || null,
    coverPath: coverPath || null,
    latitude: lat,
    longitude: lng,
    is_free: !!row.is_free,
    website: row.website_url || "",
    website_url: row.website_url || "",
    ticket_url: row.ticket_url || "",
    instagram_url: row.instagram_url || "",
    facebook_url: row.facebook_url || "",
    booking_link: row.ticket_url || row.website_url || "",
    booking_label: row.ticket_url ? "Get Tickets" : row.website_url ? "Visit Website" : "",
    start_iso: startAt || null,
    end_iso: endAt || null,
    start_date: s.date,
    start_time: s.time,
    end_date: e.date,
    end_time: e.time,
    date: shortDate(s.date),
    status: row.status,
    created_at: row.created_at || null,
    organiser_name: row.organiser_name || "",
    timezone: row.timezone || null,
    live:
      startAt != null &&
      endAt != null &&
      new Date(startAt).getTime() <= Date.now() &&
      Date.now() < new Date(endAt).getTime(),
    occurrences: occurrences || null,
    featured: false,
  };
}

// One cheap batched query → { eventId: storage_path } for the position-1 cover.
// No signing here (that happens lazily per rendered card), so this stays a single
// request regardless of how many events exist.
async function mapQueryPages(makeQuery, signal, onPage) {
  const rows = [];
  for (let offset = 0; ; ) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    const { data, error } = await makeQuery().range(offset, offset + 499).abortSignal(signal);
    if (error) throw error;
    if (!data?.length) return rows;
    rows.push(...data);
    if (onPage) await onPage(data);
    if (data.length < 500) return rows;
    offset += data.length;
  }
}

// Keep enrichment requests bounded, but let each finished batch render immediately.
async function eventBatches(rows, signal, visit) {
  let next = 0;
  await Promise.all(Array.from({ length: Math.min(4, Math.ceil(rows.length / 100)) }, async () => {
    while (next < rows.length) {
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
      const start = next;
      next += 100;
      await visit(rows.slice(start, start + 100));
    }
  }));
}
async function listingCoverPathsBatch(eventIds) {
  const result = {};
  if (!eventIds.length) return result;
  const { data: imgs, error } = await supabase
    .from("Event_Images")
    .select("event_id,storage_path,position")
    .in("event_id", eventIds);
  if (error || !imgs) return result;
  const chosen = {};
  imgs.forEach((img) => {
    const cur = chosen[img.event_id];
    if (!cur || (img.position ?? 99) < (cur.position ?? 99)) chosen[img.event_id] = img;
  });
  Object.entries(chosen).forEach(([eventId, img]) => {
    if (img.storage_path) result[eventId] = img.storage_path;
  });
  return result;
}

// Signed display-sized cover URLs for a SMALL set of ids (My Events list, edit
// prefill). Reuses the shared cache/signer; not used for the main Discover feed.
export async function coverUrlsFor(eventIds) {
  const paths = await coverPathsFor(eventIds);
  const result = {};
  await Promise.all(
    Object.entries(paths).map(async ([id, path]) => {
      const url = await resolveCover(path, false);
      if (url) result[id] = url;
    })
  );
  return result;
}

// Public read: only status = 'active' events that have not yet ended
// (now < end_at). Past events stay in the DB but drop out of discovery.
// Capped so the initial dataset stays bounded as the DB grows into the thousands.
function isDiscoverLoadFailure(error) {
  return !!error && (error.name === "TypeError" ||
    /failed to fetch|fetch failed|network|load failed|abort|timeout|timed out/i.test(
      [error.name, error.message, error.details].filter(Boolean).join(" ")
    ));
}

// Only Discover opts into the deadline and one automatic retry.
export async function fetchDiscoverEvents(country = "GB", onProgress, signal) {
  const attempt = async () => {
    const controller = new AbortController();
    const cancel = () => controller.abort();
    signal?.addEventListener("abort", cancel, { once: true });
    if (signal?.aborted) cancel();
    let timer;
    const deadline = new Promise((_, reject) => {
      timer = setTimeout(() => {
        const error = new Error("Discover event load timed out");
        error.name = "TimeoutError";
        controller.abort();
        reject(error);
      }, 12000);
    });
    try {
      // Shares the one country dataset with the feed / Search / Calendar instead of
      // downloading every event in the country a second time.
      return await Promise.race([fetchCalendarEvents(country, onProgress, 0, [], controller.signal), deadline]);
    } finally {
      clearTimeout(timer);
      controller.abort();
      signal?.removeEventListener("abort", cancel);
    }
  };
  try {
    return await attempt();
  } catch (error) {
    if (signal?.aborted || !isDiscoverLoadFailure(error)) throw error;
    return attempt();
  }
}

async function listingOccurrencesBatch(eventIds) {
  const result = {};
  if (!eventIds.length) return result;
  const { data, error } = await supabase
    .from("Event_Occurrences")
    .select("event_id,start_at,end_at")
    .in("event_id", eventIds);
  if (error || !data) return result;
  data.forEach((o) => {
    (result[o.event_id] = result[o.event_id] || []).push({ start_at: o.start_at, end_at: o.end_at });
  });
  return result;
}
async function futureOccurrenceEventIds(nowIso) {
  const { data } = await supabase
    .from("Event_Occurrences")
    .select("event_id")
    .gt("end_at", nowIso);
  return [...new Set((data || []).map((o) => o.event_id))];
}
// Columns the list cards, map pins and Search read (see mapRow + eventSearch). Admin /
// scraper-only columns are no longer downloaded for every event. If the view ever
// lacks one of these, fall back to "*" once so loading never breaks.
const LIST_COLUMNS = "id,slug,owner_id,name,description,category,venue_name,address,postcode," +
  "country_code,external_image_url,latitude,longitude,is_free,website_url,ticket_url,instagram_url," +
  "facebook_url,start_at,end_at,status,created_at,organiser_name,timezone";
let _listColumnsOk = true;
const listColumns = () => (_listColumnsOk ? LIST_COLUMNS : "*");
function isMissingColumn(error) {
  return !!error && (error.code === "42703" || error.code === "PGRST204" ||
    /column .* does not exist|could not find .* column/i.test(error.message || ""));
}

// One shared copy of each country's event list for Discover (feed + Happening Near
// You), Search and Calendar, so switching tabs or coming back to a screen reuses it
// instead of downloading the whole country again. Refreshed after DATASET_TTL.
const DATASET_TTL = 5 * 60 * 1000;
const _countryData = new Map(); // country -> { at, rows, done, promise, listeners }

// Ids of events with an occurrence that hasn't ended, paged so none are cut off.
let _futureOccurrenceIds = null; // { at, promise }
function cachedFutureOccurrenceEventIds() {
  if (_futureOccurrenceIds && Date.now() - _futureOccurrenceIds.at < DATASET_TTL) return _futureOccurrenceIds.promise;
  const nowIso = new Date().toISOString();
  const entry = { at: Date.now() };
  entry.promise = mapQueryPages(() => supabase.from("Event_Occurrences").select("event_id")
    .gt("end_at", nowIso).order("event_id").order("start_at").order("end_at"))
    .then((rows) => [...new Set(rows.map((o) => o.event_id))]);
  entry.promise.catch(() => { if (_futureOccurrenceIds === entry) _futureOccurrenceIds = null; });
  _futureOccurrenceIds = entry;
  return entry.promise;
}

export async function fetchEvents(country="GB",options=null) {
  const context = options?.context || {};
  const nowIso = context.nowIso || (context.nowIso = new Date().toISOString());
  const recurringIds = context.recurringIds || (context.recurringIds = await futureOccurrenceEventIds(nowIso));
  let q = supabase
    .from("public_events")
    .select(options?.metadataOnly?"id,name,description,category,venue_name,address,postcode,latitude,longitude,start_at,end_at,timezone":"*",options?.countOnly?{count:"exact",head:true}:{})
    .eq("status", "active")
    .eq("country_code", country);
  q = recurringIds.length
    ? q.or(`end_at.gt.${nowIso},id.in.(${recurringIds.join(",")})`)
    : q.gt("end_at", nowIso);
  if (options?.category && !["All", "Live Now", "Coming Up"].includes(options.category)) q = q.eq("category", options.category);
  q = q.order("start_at", { ascending: true });
  if (options) q = q.order("id");
  if (options && !options.countOnly) q = q.range(options.offset || 0, (options.offset || 0) + 11);
  if (options?.signal) q = q.abortSignal(options.signal);
  const { data, error, count } = await q;
  if (options?.countOnly) { if (error) throw error; return count; }
  if (options && error) throw error;
  if (error || !data) {
    // eslint-disable-next-line no-console
    console.warn("[RoadRnr] fetchEvents failed:", error);
    return [];
  }
  // Attach cover storage PATHS + occurrence dates (two batched queries, no signing)
  // so pins/cards render immediately with the current/next occurrence date.
  const ids = data.map((r) => r.id);
  const [paths, occMap] = await Promise.all([options ? (options.metadataOnly ? Promise.resolve({}) : coverPathsFor(ids).catch(() => ({}))) : coverPathsFor(ids), options ? occurrencesFor(ids).catch(() => ({})) : occurrencesFor(ids)]);
  const now = Date.now();
  return data.map((row) => {
    const cn = effectiveOf(row, occMap, now);
    return mapRow(row, { coverPath: paths[row.id], start: cn.start_at, end: cn.end_at });
  });
}
// Map markers. By default (bounds given) loads ONLY active, not-yet-ended events
// whose stored latitude/longitude fall inside the currently visible map area, so the
// map scales to thousands of events without a global cap. Pass `ids` to load a
// specific set regardless of bounds (used to keep boosted pins visible everywhere,
// unchanged). With neither argument it loads the whole active set (legacy behaviour).
// Skips cover signing (markers don't render covers) to stay light.
const _mapAreas = []; // recently loaded map areas: { bounds, at, events }
const _within = (outer, inner) => outer.west <= outer.east && inner.west <= inner.east &&
  outer.south <= inner.south && outer.north >= inner.north && outer.west <= inner.west && outer.east >= inner.east;
const _inBounds = (b) => (e) => e.latitude >= b.south && e.latitude <= b.north && e.longitude >= b.west && e.longitude <= b.east;

export async function fetchMapEvents(bounds = null, ids = null, options = {}) {
  const areaQuery = bounds && !ids?.length && !options.liveOnly;
  if (areaQuery) {
    const hit = _mapAreas.find((a) => Date.now() - a.at < DATASET_TTL && _within(a.bounds, bounds));
    if (hit) {
      const events = hit.events.filter(_inBounds(bounds));
      if (!options.signal?.aborted) options.onProgress?.(events);
      return events;
    }
  }
  let events;
  const usedListColumns = _listColumnsOk;
  try {
    events = await fetchMapEventsFromSupabase(bounds, ids, options);
  } catch (error) {
    if (!usedListColumns || !isMissingColumn(error)) throw error;
    _listColumnsOk = false;
    events = await fetchMapEventsFromSupabase(bounds, ids, options);
  }
  if (areaQuery && !options.signal?.aborted) {
    _mapAreas.unshift({ bounds, at: Date.now(), events });
    _mapAreas.length = Math.min(_mapAreas.length, 6);
  }
  return events;
}

async function fetchMapEventsFromSupabase(bounds, ids, options) {
  const { liveOnly = false, signal } = options;
  if (!bounds && !ids?.length) throw new Error("Map loading requires bounds or explicit event IDs");
  const now = Date.now();
  const nowIso = new Date(now).toISOString();
  const boundedQuery = () => {
    let q = supabase.from("public_events").select(listColumns()).eq("status", "active");
    if (ids?.length) q = q.in("id", ids);
    else {
      q = q.gte("latitude", bounds.south).lte("latitude", bounds.north);
      if (bounds.west <= bounds.east) q = q.gte("longitude", bounds.west).lte("longitude", bounds.east);
      else q = q.or('longitude.gte.' + bounds.west + ',longitude.lte.' + bounds.east);
    }
    return q;
  };
  let rows;
  if (liveOnly) {
    const [base, liveOccurrences] = await Promise.all([
      mapQueryPages(() => boundedQuery().lte("start_at", nowIso).gt("end_at", nowIso)
        .order("start_at").order("id"), signal),
      mapQueryPages(() => supabase.from("Event_Occurrences").select("event_id,start_at,end_at")
        .lte("start_at", nowIso).gt("end_at", nowIso)
        .order("event_id").order("start_at").order("end_at"), signal),
    ]);
    const recurringIds = [...new Set(liveOccurrences.map((o) => o.event_id))];
    const byId = new Map(base.map((row) => [row.id, row]));
    for (let i = 0; i < recurringIds.length; i += 100) {
      const batch = recurringIds.slice(i, i + 100);
      const extra = await mapQueryPages(() => boundedQuery().in("id", batch)
        .order("start_at").order("id"), signal);
      extra.forEach((row) => byId.set(row.id, row));
    }
    rows = [...byId.values()];
  } else {
    const ready = new Map();
    const enrichment = [];
    const publish = () => {
      if (!signal?.aborted && options.onProgress) options.onProgress([...ready.values()]);
    };
    // Query the padded area immediately; secondary dates never gate useful pins.
    const onPage = (page) => {
      page.forEach((row) => {
        if (new Date(row.end_at).getTime() > now) {
          // Use the row's OWN span for the first paint instead of forcing live=false:
          // most events are single-occurrence, so row.start_at/end_at already give the
          // correct Live Now state immediately. Only recurring events (whose row span
          // can be stale) need the async occurrence enrichment below to correct it —
          // forcing every pin through a false "not live" state first caused genuinely
          // live pins to flicker off and back on on every pan/zoom.
          const event = mapRow(row);
          event.occurrencePending = true;
          ready.set(row.id, event);
        }
      });
      publish();
      const task = eventBatches(page, signal, async (batch) => {
        const occMap = await mapOccurrencesFor(batch.map((row) => row.id), signal);
        batch.forEach((row) => {
          const cn = effectiveOf(row, occMap, now);
          if (new Date(cn.end_at || row.end_at).getTime() > now) {
            ready.set(row.id, mapRow(row, { start: cn.start_at, end: cn.end_at }));
          } else ready.delete(row.id);
        });
        publish();
      });
      enrichment.push(task.then(() => null, (error) => error));
    };
    rows = await mapQueryPages(() => boundedQuery().gt("end_at", nowIso).order("start_at").order("id"), signal, onPage);
    // Recover recurring events with stale row spans only after useful pins publish.
    // Only events that still have an upcoming occurrence can show, so load just those
    // ids instead of every already-ended event in the visible area.
    const recurringIds = await cachedFutureOccurrenceEventIds();
    for (let i = 0; i < recurringIds.length; i += 100) {
      const batch = recurringIds.slice(i, i + 100);
      await mapQueryPages(() => boundedQuery().lte("end_at", nowIso).in("id", batch).order("start_at").order("id"), signal, onPage);
    }
    const errors = await Promise.all(enrichment);
    const error = errors.find(Boolean);
    if (error) throw error;
    return [...ready.values()];
  }
  const occMap = {};
  await eventBatches(rows, signal, async (page) => {
    const batch = page.map((row) => row.id);
    const occs = await mapQueryPages(() => supabase.from("Event_Occurrences")
      .select("event_id,start_at,end_at").in("event_id", batch)
      .order("event_id").order("start_at").order("end_at"), signal);
    occs.forEach((o) => { (occMap[o.event_id] ||= []).push(o); });
  });
  const mapped = rows.map((row) => {
    const cn = effectiveOf(row, occMap, now);
    return mapRow(row, { start: cn.start_at, end: cn.end_at });
  });
  return liveOnly ? mapped.filter((event) => event.live) : mapped;
}


// Calendar page: same public visibility rules as fetchEvents (active + not yet
// ended, capped 500), but keeps each event's RAW occurrence spans so the calendar
// can map single/recurring/multi-day events onto their actual dates. Single-date
// events fall back to the row span. `created_at` is carried for the NEW badge.
// Reuses the existing batched cover-path + occurrence queries (no extra request).
// Page size for the Supabase range query. Raised from 12 to 200: Calendar/Search/
// Discover now wait for the FULL set before rendering (so the grid/feed doesn't
// reshuffle mid-stream), which made the old 12-row page size very slow for large
// countries — GB (~490 active events) needed ~41 sequential round trips (each with
// its own cover-path + occurrence queries) before anything appeared. 200 cuts that
// to ~3 round trips for GB and ~4 for a larger country like the US, while still
// paging (not one unbounded query) so it keeps scaling as the dataset grows.
const CALENDAR_PAGE_SIZE = 200;
export function fetchCalendarEvents(country = "GB", onProgress, offset = 0, previous = [], signal) {
  let entry = _countryData.get(country);
  if (!entry || (entry.done && Date.now() - entry.at > DATASET_TTL)) {
    const fresh = { at: Date.now(), rows: [], done: false, listeners: new Set() };
    fresh.promise = fetchCountryPages(country, (rows) => {
      fresh.rows = rows;
      fresh.listeners.forEach((listener) => listener(rows));
    }, 0, [], undefined, {})
      .then((rows) => { fresh.rows = rows; fresh.done = true; fresh.at = Date.now(); return rows; })
      .catch((error) => { if (_countryData.get(country) === fresh) _countryData.delete(country); throw error; })
      .finally(() => fresh.listeners.clear());
    _countryData.set(country, fresh);
    entry = fresh;
  }
  if (entry.done) {
    if (!signal?.aborted) onProgress?.(entry.rows);
    return Promise.resolve(entry.rows);
  }
  if (onProgress) {
    if (entry.rows.length) onProgress(entry.rows);
    entry.listeners.add(onProgress);
    signal?.addEventListener("abort", () => entry.listeners.delete(onProgress), { once: true });
  }
  return entry.promise;
}

async function fetchCountryPages(country, onProgress, offset, previous, signal, context) {
  const nowIso = context.nowIso || (context.nowIso = new Date().toISOString());
  const recurringIds = context.recurringIds || (context.recurringIds = await futureOccurrenceEventIds(nowIso));
  const columns = listColumns();
  let q = supabase
    .from("public_events")
    .select(columns)
    .eq("status", "active")
    .eq("country_code", country);
  q = recurringIds.length
    ? q.or(`end_at.gt.${nowIso},id.in.(${recurringIds.join(",")})`)
    : q.gt("end_at", nowIso);
  if (signal) q = q.abortSignal(signal);
  const { data, error } = await q.order("start_at", { ascending: true }).order("id").range(offset, offset + CALENDAR_PAGE_SIZE - 1);
  if (columns !== "*" && isMissingColumn(error)) { _listColumnsOk = false; return fetchCountryPages(country, onProgress, offset, previous, signal, context); }
  if (error) throw error; if (signal?.aborted || !data?.length) return previous;
  const ids = data.map((r) => r.id);
  const [paths, occMap] = await Promise.all([coverPathsFor(ids).catch(() => ({})), occurrencesFor(ids).catch(() => ({}))]);
  const now = Date.now();
  const batch = data.map((row) => {
    const list = occMap[row.id]?.length
      ? occMap[row.id]
      : [{ start_at: row.start_at, end_at: row.end_at }];
    const cn = pickCurrentNext(list, now);
    const ev = mapRow(row, { coverPath: paths[row.id], start: cn.start_at, end: cn.end_at });
    ev.occurrenceList = list;
    return ev;
  });
  const loaded = [...previous, ...batch];
  if (!signal?.aborted) onProgress?.(loaded);
  if (data.length < CALENDAR_PAGE_SIZE || signal?.aborted) return loaded;
  return fetchCountryPages(country, onProgress, offset + CALENDAR_PAGE_SIZE, loaded, signal, context);
}

// Return a shallow clone of a mapped event with its date/time/live fields set to
// a SPECIFIC occurrence — so a card shown under a selected calendar day reflects
// that day's occurrence rather than the event's current/next one. Id/slug are
// unchanged, so tapping still opens the same single Event Detail page.
export function occurrenceVariant(event, occ) {
  const s = splitIso(occ.start_at, event.timezone);
  const e = splitIso(occ.end_at, event.timezone);
  const now = Date.now();
  return {
    ...event,
    start_iso: occ.start_at || null,
    end_iso: occ.end_at || null,
    start_date: s.date,
    start_time: s.time,
    end_date: e.date,
    end_time: e.time,
    date: shortDate(s.date),
    live:
      occ.start_at != null &&
      occ.end_at != null &&
      new Date(occ.start_at).getTime() <= now &&
      now < new Date(occ.end_at).getTime(),
  };
}

// Saved Events: fetch ONLY the given ids (not the whole active dataset) so the
// payload/coverPaths work is bounded by what the user actually saved. Same public
// visibility rules as fetchEvents (active + not yet ended); images sign lazily.
export async function fetchEventsByIds(ids) {
  if (!ids || !ids.length) return [];
  const { data, error } = await supabase
    .from("public_events")
    .select("*")
    .in("id", ids)
    .eq("status", "active")
    .gt("end_at", new Date().toISOString());
  if (error || !data) return [];
  const foundIds = data.map((r) => r.id);
  const [paths, occMap] = await Promise.all([coverPathsFor(foundIds), occurrencesFor(foundIds)]);
  const now = Date.now();
  return data.map((row) => {
    const cn = effectiveOf(row, occMap, now);
    return mapRow(row, { coverPath: paths[row.id], start: cn.start_at, end: cn.end_at });
  });
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export async function fetchEvent(idOrSlug) {
  // Resolve by UUID (existing links) OR by slug (clean canonical URLs).
  const col = UUID_RE.test(String(idOrSlug)) ? "id" : "slug";
  const { data, error } = await supabase
    .from("public_events")
    .select("*")
    .eq(col, idOrSlug)
    .eq("status", "active")
    .maybeSingle();
  if (error || !data) return null;

  const id = data.id;
  let heroUrl = null;
  const { data: imgs } = await supabase
    .from("Event_Images")
    .select("storage_path,position")
    .eq("event_id", id)
    .order("position", { ascending: true })
    .limit(1);
  if (imgs && imgs[0]?.storage_path) {
    // Detail hero: a larger display-sized transform (cached/deduped), still far
    // smaller than the full-resolution original.
    heroUrl = await resolveCover(imgs[0].storage_path, true);
  }
  // Current/next occurrence = main displayed date; remaining upcoming = "More dates".
  const occRows = await occurrencesFor([id]);
  const occs = occRows[id]?.length ? occRows[id] : [{ start_at: data.start_at, end_at: data.end_at }];
  const now = Date.now();
  const cn = pickCurrentNext(occs, now);
  const moreDates = upcomingOccurrences(occs, now).filter(
    (o) => !(o.start_at === cn.start_at && o.end_at === cn.end_at)
  );
  return mapRow(data, { image: heroUrl, start: cn.start_at, end: cn.end_at, occurrences: moreDates, hero: true });
}

// Read the event owner's PUBLIC organiser info (name / bio / image). Fetched via
// the service_role backend proxy because Supabase RLS restricts direct Profiles
// reads to the row owner, so the public client returns no row for other owners.
// Read-only — never modifies profile data. Returns { name, bio, imageUrl } or null.
export async function fetchOrganiser(ownerId) {
  if (!ownerId) return null;
  try {
    const { data } = await axios.get(`${API}/organiser/${ownerId}`);
    const name = data?.name || "";
    const bio = data?.bio || "";
    const imageUrl = data?.image_url || null;
    if (!name && !bio && !imageUrl) return null;
    return { name, bio, imageUrl };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("[RoadRnr] fetchOrganiser failed:", error);
    return null;
  }
}

// Featured = events with a CURRENTLY ACTIVE boost in the existing Event_Boosts
// structure (starts_at <= now < ends_at). Normal active events are NEVER
// auto-featured. The list of active boosted event ids is resolved by the backend
// (service_role) so Supabase RLS is unchanged; the events themselves are read
// publicly here exactly as before. No Stripe/payment activation happens here.
export async function fetchFeatured() {
  try {
    let activeIds = [];
    try {
      const { data } = await axios.get(`${API}/boost/active`);
      activeIds = Array.isArray(data?.event_ids) ? data.event_ids : [];
    } catch {
      activeIds = [];
    }
    if (!activeIds.length) return [];

    const { data: rows } = await supabase
      .from("public_events")
      .select("*")
      .in("id", activeIds)
      .eq("status", "active");
    if (!rows || !rows.length) return [];

    const ids = rows.map((r) => r.id);
    const [paths, occMap] = await Promise.all([coverPathsFor(ids), occurrencesFor(ids)]);
    const now = Date.now();
    return rows.map((row) => {
      const cn = effectiveOf(row, occMap, now);
      return { ...mapRow(row, { coverPath: paths[row.id], start: cn.start_at, end: cn.end_at }), featured: true };
    });
  } catch {
    return [];
  }
}

// Active boost event ids (public, resolved server-side via service_role so the
// list is identical for every visitor — logged in or out). The Featured cards are
// derived from these ids against the already-public events set.
export async function fetchActiveBoostIds() {
  try {
    const { data } = await axios.get(`${API}/boost/active`);
    return Array.isArray(data?.event_ids) ? data.event_ids : [];
  } catch {
    return [];
  }
}

// No-op retained for compatibility (Supabase events have no view counter).
export async function trackView() {}

// Community "Submit an Event" dialog (legacy) — unchanged.
export async function submitEvent(payload) {
  const { data } = await axios.post(`${API}/submissions`, payload);
  return data;
}

// Listing enrichment stays bounded; selection and fallback behavior are unchanged.
// Chunked at 300 ids per request (well within what a Supabase `.in()` filter takes
// comfortably) and run in parallel, not sequentially — the old 12-per-chunk,
// one-at-a-time loop meant a single page of events (now up to CALENDAR_PAGE_SIZE)
// still needed dozens of sequential round trips for cover paths and occurrences
// alone, which undid the point of fetching larger pages.
async function listingEnrichment(ids, load) {
  const result = {};
  const chunks = [];
  for (let offset = 0; offset < ids.length; offset += 300) chunks.push(ids.slice(offset, offset + 300));
  const parts = await Promise.all(chunks.map((chunk) => load(chunk)));
  parts.forEach((part) => Object.assign(result, part));
  return result;
}
export function occurrencesFor(ids) { return listingEnrichment(ids, listingOccurrencesBatch); }
export function coverPathsFor(ids) { return listingEnrichment(ids, listingCoverPathsBatch); }
